import { useState } from 'react';
import { useCustom } from '@refinedev/core';
import { Card, Flex, Segmented, Space, Typography } from 'antd';
import { DualAxes, Line } from '@ant-design/charts';
import { BOTTOM_LEGEND } from '@/pages/dashboard/chartLegend';
import type { RevenueTrendStatsOut } from '@/api/generated/schemas';
import { formatLedger, formatPayable } from '@/utils/money';
import { cleanFilters, type SettlementStatsFilters } from './filters';
import { fillMonthlySeries, type MonthlyPoint } from './seriesFill';

const PERIODS = [
  { label: '6 mies.', value: 6 },
  { label: '12 mies.', value: 12 },
  { label: '24 mies.', value: 24 },
];

// Stałe hue per metryka (nie cykl G2); paleta zwalidowana pod CVD.
const BAR_COLOR = '#1783FF';
const BAR_COLOR_OPEN = '#69B1FF';
const RATE_COLOR = '#F0884D';
const PUBLISHERS_COLOR = '#00C9C9';
const PRODUCTS_COLOR = '#D580FF';

const REACH_SERIES = [
  { key: 'publisherCount', label: 'Wydawcy' },
  { key: 'productCount', label: 'Produkty' },
] as const;

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <Flex align="center" gap={6}>
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        display: 'inline-block',
      }}
    />
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {label}
    </Typography.Text>
  </Flex>
);

type Props = { filters: SettlementStatsFilters };

export const SettlementRevenueTrendCard = ({ filters }: Props) => {
  const [months, setMonths] = useState(12);

  const { query } = useCustom<RevenueTrendStatsOut>({
    url: 'settlement/charges_monthly/stats/revenue_trend',
    method: 'get',
    config: { query: { months, ...cleanFilters(filters) } },
  });

  const data = fillMonthlySeries(query.data?.data.entries ?? [], months);

  const reachData = data.flatMap((point) =>
    REACH_SERIES.map((series) => ({
      month: point.month,
      type: series.label,
      value: point[series.key],
    }))
  );

  return (
    <Card
      title="Trend przychodów"
      extra={
        <Segmented
          options={PERIODS.map((p) => ({ label: p.label, value: p.value }))}
          value={months}
          onChange={(v) => setMonths(v as number)}
        />
      }
      loading={query.isLoading}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Flex gap={16} wrap>
          <LegendDot
            color={BAR_COLOR}
            label="Kwota z faktury (słupki, lewa oś)"
          />
          <LegendDot
            color={BAR_COLOR_OPEN}
            label="Bieżący miesiąc — w toku, dane rosną"
          />
          <LegendDot
            color={RATE_COLOR}
            label="Stawka efektywna PLN/h (linia, prawa oś)"
          />
        </Flex>

        <DualAxes
          data={data}
          xField="month"
          height={320}
          legend={false}
          children={[
            {
              type: 'interval',
              // The headline series is the invoice; the ledger rides along in
              // the tooltip.
              yField: 'payableAmount',
              style: {
                fill: (d: MonthlyPoint) =>
                  d.current ? BAR_COLOR_OPEN : BAR_COLOR,
                radiusTopLeft: 4,
                radiusTopRight: 4,
              },
              label: {
                text: (d: MonthlyPoint) => (d.current ? 'w toku' : ''),
                position: 'top',
                style: { fill: '#8c8c8c', fontSize: 11, dy: -4 },
              },
              axis: { y: { title: 'Kwota z faktury (PLN)' } },
              tooltip: {
                items: [
                  (d: MonthlyPoint) => ({
                    name: 'Kwota (faktura)',
                    value: d.current
                      ? `${formatPayable(d.payableAmount)} (w toku)`
                      : formatPayable(d.payableAmount),
                    color: d.current ? BAR_COLOR_OPEN : BAR_COLOR,
                  }),
                  (d: MonthlyPoint) => ({
                    name: 'Kwota (księga)',
                    value: formatLedger(d.amount),
                    color: BAR_COLOR,
                  }),
                  (d: MonthlyPoint) => ({
                    name: 'Godziny',
                    value: `${(d.basisSeconds / 3600).toFixed(1)} h`,
                    color: BAR_COLOR,
                  }),
                  (d: MonthlyPoint) => ({
                    name: 'Wydawcy',
                    value: `${d.publisherCount}`,
                    color: PUBLISHERS_COLOR,
                  }),
                  (d: MonthlyPoint) => ({
                    name: 'Produkty',
                    value: `${d.productCount}`,
                    color: PRODUCTS_COLOR,
                  }),
                  (d: MonthlyPoint) => ({
                    name: 'Stawka efektywna',
                    // A rate, so the ledger scale — not an invoice amount.
                    value: d.rate != null ? `${formatLedger(d.rate)}/h` : '—',
                    color: RATE_COLOR,
                  }),
                ],
              },
            },
            {
              type: 'line',
              yField: 'rate',
              style: { stroke: RATE_COLOR, lineWidth: 2 },
              axis: { y: { position: 'right', title: 'Stawka (PLN/h)' } },
              tooltip: false,
            },
          ]}
        />

        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Zasięg bazy rozliczanej — rosnące kwoty przy płaskiej liczbie
            wydawców oznaczają koncentrację.
          </Typography.Text>
          <Line
            data={reachData}
            xField="month"
            yField="value"
            colorField="type"
            height={140}
            scale={{
              color: {
                domain: REACH_SERIES.map((s) => s.label),
                range: [PUBLISHERS_COLOR, PRODUCTS_COLOR],
              },
            }}
            legend={BOTTOM_LEGEND}
            style={{ lineWidth: 2 }}
          />
        </div>
      </Space>
    </Card>
  );
};
